/**
 * Rich `prefillAnswers` fixtures aligned with estate-client-schemas `data-model.json`
 * `prefillSource` sample (read-only suggestions at session start).
 *
 * Testator and spouse are Person-ID references — per `data-model-contract.md`'s
 * `personReference` contract, `testator.profile.personId` is a bare personId
 * string (not `{ personId }`-wrapped: `profile` already groups it with its
 * sibling `address`), while `testator.spouse` keeps the `{ personId }` wrapper.
 * The backend seeds `people` with the testator (`relationship: "testator"`),
 * the suggested spouse (no `relationship` until the user confirms/adds them
 * via `step_confirm_spouse`/spouse-details), and children (`relationship:
 * "child"`). `profile.address` is unaffected and stays on `testator`, not on
 * the Person. `children.childrenList[]` items are `{ personId, parentage }` —
 * the backend sends `parentage` directly on the item (see `personReference`'s
 * `itemScopedFields`), never on the Person.
 */
import type {EstateAnswersBySection} from './types';

const SHARED_PEOPLE = [
  {
    id: 'person_1',
    fullName: 'John Smith',
    dateOfBirth: '01/01/1985',
    firstName: 'John',
    middleName: '',
    lastName: 'Smith',
    relationship: 'testator',
  },
  {
    id: 'person_2',
    fullName: 'Jane Smith',
    firstName: 'Jane',
    middleName: 'Marie',
    lastName: 'Smith',
    dateOfBirth: '03/15/1988',
    email: 'jane.smith@email.com',
  },
  {id: 'person_3', fullName: 'Michael Smith', dateOfBirth: '06/10/1975'},
  {id: 'person_4', fullName: 'Sarah Jones', dateOfBirth: '09/22/1982'},
  // No `relationship` yet — like the suggested spouse, a prefilled child
  // only gets `relationship: "child"` stamped once the user saves the
  // children list (see `personReference` in family-template.json and
  // `prefillPromotion.stampPersonReferenceRelationshipsForCheckpoint`).
  {id: 'child_a1b2c3', fullName: 'Emma Smith', dateOfBirth: '2015-03-14'},
  {id: 'child_d4e5f6', fullName: 'Liam Smith', dateOfBirth: '2018-07-22'},
];

const SHARED_CHILDREN = {
  hasChildren: 'true',
  childrenList: [
    {personId: 'child_a1b2c3', parentage: 'both'},
    {personId: 'child_d4e5f6', parentage: 'self_other'},
  ],
};

const SHARED_TESTATOR_PREFILL = {
  profile: {
    personId: 'person_1',
    address: {
      country: 'United States',
      address: '123 Main St',
      address2: '',
      city: 'Los Angeles',
      state: 'CA',
      zip: '90210',
    },
  },
  primaryAddressConfirmed: 'true',
  relationshipStatus: 'married',
  spouseConfirmed: 'true',
  spouse: {personId: 'person_2'},
};

function sharedPrefillBase(): EstateAnswersBySection {
  return {
    people: SHARED_PEOPLE,
    children: SHARED_CHILDREN,
    // guardians: {
    //   nominateGuardians: 'true',
    //   guardians: [{personId: 'person_3', role: 'guardian'}],
    // },
    // beneficiaries: {
    //   beneficiaries: [],
    //   hasBackupBeneficiary: false,
    //   minimumAgeDistribution: '18',
    // },
    // pourOverWill: {
    //   executors: [{personId: 'person_3', role: 'executor'}],
    // },
    // financialPOA: {
    //   poa: [
    //     {personId: 'person_3', role: 'primary_poa'},
    //     {personId: 'person_4', role: 'successor_poa'},
    //   ],
    // },
    // health: {
    //   poa: [
    //     {personId: 'person_3', role: 'primary_poa'},
    //     {personId: 'person_4', role: 'successor_poa'},
    //   ],
    //   lifeSustainingTreatment: 'no_prolonging',
    //   artificialNutritionPreference: 'no_if_imminent',
    //   organDonationPreference: 'donate_all',
    //   painMedication: true,
    //   authorizedPersons: [{personId: 'person_3', role: 'authorized_designee'}],
    //   successorTrusteeMedicalAccess: true,
    //   medicalRecordsExpiration: 'no_expiration',
    // },
    testator: SHARED_TESTATOR_PREFILL,
  };
}

/** Will-package prefill suggestions. */
export function buildWillPrefillAnswers(): EstateAnswersBySection {
  return sharedPrefillBase();
}

/** Trust-package prefill suggestions — includes trust + spouse reciprocal stubs. */
export function buildTrustPrefillAnswers(): EstateAnswersBySection {
  return {
    ...sharedPrefillBase(),
    trust: {
      name: 'The Smith Family Trust',
      type: 'joint',
      primaryTrustees: [{personId: 'person_1'}, {personId: 'person_2'}],
      trustees: [{personId: 'person_3', role: 'successor_trustee'}],
    },
    spousePourOverWill: {
      executors: [{personId: 'person_2', role: 'executor'}],
    },
    spouseFinancialPOA: {
      poa: [{personId: 'person_2', role: 'primary_poa'}],
    },
    spouseHealth: {
      poa: [{personId: 'person_2', role: 'primary_poa'}],
      lifeSustainingTreatment: 'no_prolonging',
      artificialNutritionPreference: 'no_if_imminent',
      organDonationPreference: 'donate_all',
      painMedication: true,
      authorizedPersons: [{personId: 'person_2', role: 'authorized_designee'}],
      medicalRecordsExpiration: 'no_expiration',
    },
  };
}
